import { entityStore } from '~/utility/BinSentry-ui-utility';
import { BnModalMixin } from '../../common/bn-modal-mixin';
import { html, LitEntity, renderIf } from '../../utility/lit-entity';
import { formStyles } from '../../styles/form-styles';
import '../../common/bn-flash';

class MtechProteinIntegrationModal extends BnModalMixin(LitEntity) {
  constructor() {
    super();
    this.id = 'mtech-protein-integration-modal';
  }

  static get properties() {
    return {
      applyingFtpConfiguration: { type: Boolean, attribute: false },
      downloadingCsv: { type: Boolean, attribute: false },
      publicKey: { type: String, attribute: false },
    };
  }

  renderContents() {
    return html`
      <style>
        ${formStyles}
        bn-modal#mtech-protein-integration-modal bn-flash {
          padding: 0 10px;
        }
        bn-modal#mtech-protein-integration-modal .description {
          padding: 0 20px 10px 20px;
        }
        bn-modal#mtech-protein-integration-modal .status {
          font-weight: bold;
        }
        bn-modal#mtech-protein-integration-modal .external-link {
          margin-left: 0.5rem;
        }
        bn-modal#mtech-protein-integration-modal .sample-csv-info {
          padding-left: 1rem;
        }
        bn-modal#mtech-protein-integration-modal .action-surface {
          position: relative;
        }
        bn-modal#mtech-protein-integration-modal .hidden {
          visibility: hidden;
        }
        bn-modal#mtech-protein-integration-modal .load-spinner {
          position: absolute;
          width: 100%;
        }
        bn-modal#mtech-protein-integration-modal .public-key-text {
          font-family: monospace;
          overflow-wrap: anywhere;
          margin: 1rem;
          background-color: var(--gray-20);
        }
      </style>
      <h3 slot="title">${this.title}</h3>
      <div class="description">
        <p>
          The MTech Interface integration allows forwarding bin level data to an SFTP server,
          so that it can be picked up by
          <a href="http://help.mtech-systems.com/mergedProjects/Admin/System/Interfaces/Import.htm" target="_blank">
            MTech Interface Import<bn-icon class="external-link" name="far fa-external-link"></bn-icon>
          </a>
        </p>
        <p>
          SFTP server hostname and credentials must be provided for the integration to work.
          Authentication can be configured using username and password, or by just providing username
          and configuring your SFTP server to trust BinSentry public key.
          Port and directory path can also be configured.
        </p>
        <p>
          Once the SFTP credentials are configured, add the following tags to bins in order to forward bin level data to MTech:
        </p>
        <ul>
          <li>
            mtechFarmNo - unique id of the farm where the bin is located
          </li>
          <li>
            mtechHouseNo - house index within the farm
          </li>
          <li>
            mtechBinNo - bin index within the house
          </li>
        </ul>
      </div>

      <bn-flash></bn-flash>

      <bn-surface>
        <div>
          <label>Status:</label>
          <span class="status">${this.entity.properties.ftpCredentialsConfigured ? 'Enabled' : 'Disabled'}</span>
        </div>
      </bn-surface>

      <bn-surface>
        <div>
          <button class="button button--outlined" @click="${this._onClickViewPublicKey}">View Public Key</button>
          <span class="sample-csv-info">Configure the SFTP server to trust this public key as an alternative to using password</span>
          ${renderIf(this.publicKey, () => html`
            <div class="public-key-text">${this.publicKey}</div>
          `)}
        </div>
      </bn-surface>

      ${renderIf(this.entity.hasSubEntityByRel('https://api.binsentry.com/rel/sample-csv'), () => html`
        <bn-surface>
          <button class="button button--contained" @click="${this._onClickCsvDownload}" .disabled="${this.downloadingCsv}">
            <bn-icon name="fas fa-cloud-download-alt"></bn-icon>
            ${this.downloadingCsv ? html`Loading <bn-icon name="fas fa-spinner fa-pulse"></bn-icon>` : 'Download Sample CSV File'}
          </button>
          <span class="sample-csv-info">File includes sample data for all bins with mtech tags</span>
        </bn-surface>
      `)}

      ${renderIf(this.entity.hasAction('set-ftp-configuration'), () => html`
        <bn-surface class="action-surface">
          ${renderIf(this.applyingFtpConfiguration, () => html`<div class="load-spinner"><bn-loading><p>Verifying SFTP Configuration...</p></bn-loading></div>`)}
          <bn-action
            class="${this.applyingFtpConfiguration ? 'hidden' : ''}"
            .href="${this.href}"
            actionname="set-ftp-configuration"
            @submit="${this._onFtpConfigurationSubmit}"
            @response="${this._onFtpConfigurationResponse}"
            @error="${this._onFtpConfigurationError}"
            suppressflash
          >
            <h3 slot="header">
              ${this.entity.properties.ftpCredentialsConfigured ? 'Update' : 'Set'} SFTP configuration
            </h3>
          </bn-action>
        </bn-surface>
      `)}

      ${renderIf(this.entity.hasAction('delete-ftp-configuration'), () => html`
        <bn-surface>
          <bn-action
            .href="${this.href}"
            actionname="delete-ftp-configuration"
            submittext="Delete SFTP configuration"
            buttonstyle='outlinedError'
            .preSubmit="${this._confirmDeletingFtpConfiguration}"
            @response="${() => this._onResponse('MTech integration has been disabled.')}"
            @error="${(e) => this._onError(e, 'Could not delete SFTP configuration.')}"
          >
            <h3 slot="header">Disable MTech integration</h3>
          </bn-action>
        </bn-surface>
      `)}
    `;
  }

  async _onFtpConfigurationSubmit() {
    this.applyingFtpConfiguration = true;
  }

  async _onFtpConfigurationResponse() {
    this.applyingFtpConfiguration = false;
    return this._onResponse('SFTP credentials have been configured. MTech integration is enabled.');
  }

  async _onFtpConfigurationError(e) {
    this.applyingFtpConfiguration = false;
    return this._onError(e, 'Could not set SFTP configuration.');
  }

  async _onResponse(successText) {
    this._getFlash().clear();
    await entityStore.fetchWait(this.getHref('self'));
    this._getFlash().success(successText);
  }

  async _onError(event, errorText) {
    this._getFlash().clear();
    this._getFlash().critical(html`${errorText}<div><code>${event.error.message}</code></div>`);
  }

  _getFlash() {
    return this.renderRoot.querySelector('bn-flash');
  }

  _confirmDeletingFtpConfiguration() {
    const confirmMessage = 'Are you sure you want to delete MTech SFTP configuration? This will disable MTech integration.';
    return window.confirm(confirmMessage);
  }

  async _onClickCsvDownload() {
    this.downloadingCsv = true;

    try {
      const sampleCsv = this.entity.getSubEntity('https://api.binsentry.com/rel/sample-csv');
      const headers = new Headers({ Authorization: `Bearer ${(await entityStore.getToken())}` });
      const response = await fetch(sampleCsv.href, { headers });

      const downloadUrl = URL.createObjectURL(await new Response(response.body).blob());

      const ele = document.createElement('a');
      ele.style.display = 'none';
      ele.setAttribute('href', downloadUrl);
      ele.setAttribute('download', `${new Date().toISOString()}.csv`);
      ele.click();
    } catch (err) {
      this._getFlash().clear();
      this._getFlash().critical(html`Error downloading CSV File<div><code>${err.message}</code></div>`);
      throw err;
    } finally {
      this.downloadingCsv = false;
    }
  }

  async _onClickViewPublicKey() {
    const publicKeyEntity = this.entity.getSubEntity('https://api.binsentry.com/rel/public-key');
    const response = await this.fetchSiren(publicKeyEntity.href);
    this.publicKey = response.properties.publicKey;
  }
}

window.customElements.define('mtech-protein-integration-modal', MtechProteinIntegrationModal);
